import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App.jsx';
import Login from './Login.jsx';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './index.css';

const runtimeHost = typeof window !== 'undefined' ? String(window.location.hostname || '').toLowerCase() : '';
const isLiveHost = runtimeHost === 'pim.hendler.co.uk';
const isDevEnv = Boolean(
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.MODE === 'development') ||
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV === true) ||
    (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') ||
    (runtimeHost && !isLiveHost)
);

function DevBanner() {
  const [gitInfo, setGitInfo] = useState(null);

  useEffect(() => {
    if (!isDevEnv) return;
    const token = localStorage.getItem('token');
    fetch('/api/git-info', {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => setGitInfo(data))
      .catch(() => setGitInfo(null));
  }, []);

  if (!isDevEnv) return null;

  // Format the git info for display
  const gitDisplay = gitInfo?.summary
    ? gitInfo.summary
    : gitInfo?.branch
    ? `[${gitInfo.branch}] ${gitInfo.commitHash || ''}`
    : '';

  const gitTooltip = gitInfo?.commitSubject
    ? `Commit: ${gitInfo.commitSubject}\nDate: ${gitInfo.commitDateFormatted || gitInfo.commitDate || ''}`
    : '';

  return (
    <div className="pim-dev-banner" role="status" aria-label="Development environment">
      <span className="pim-dev-banner__text">DEV ENVIRONMENT</span>
      {gitDisplay && (
        <span className="pim-dev-banner__git" title={gitTooltip}>
          {gitDisplay}
        </span>
      )}
      <a
        className="pim-dev-banner__link"
        href="https://pim.hendler.co.uk/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Go to LIVE
      </a>
    </div>
  );
}

function Root() {
  useEffect(() => {
    document.documentElement.classList.toggle('pim-env-dev', isDevEnv);
  }, []);

  return (
    <BrowserRouter>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <DevBanner />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <App />
              </RequireAuth>
            }
          />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

function RequireAuth({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Render error caught:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message || 'Unknown error';
      const stack = this.state.error?.stack || '';
      const componentStack = this.state.errorInfo?.componentStack || '';

      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>Something went wrong: {message}.</h1>
          {(stack || componentStack) && (
            <div style={{ textAlign: 'left', maxWidth: 900, margin: '1.5rem auto' }}>
              {stack && (
                <>
                  <strong>Error stack</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', background: '#111', color: '#f5f5f5', padding: '1rem', borderRadius: 6 }}>
                    {stack}
                  </pre>
                </>
              )}
              {componentStack && (
                <>
                  <strong>Component stack</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', background: '#111', color: '#f5f5f5', padding: '1rem', borderRadius: 6 }}>
                    {componentStack}
                  </pre>
                </>
              )}
            </div>
          )}
          <button onClick={() => window.location.reload()}>Retry Page Load</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ErrorBoundary>
    <Root />
  </ErrorBoundary>
);
